import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { learningApi } from '../api/learningApi'
import { useAuth } from '@/core/auth/AuthContext'
import LoadingPage from '@/shared/components/LoadingPage'
import type { TrainingReportFilters, EmployeeWithCertification } from '../types/learning.types'

const currentYear = new Date().getFullYear()
const years = Array.from({ length: 5 }, (_, i) => currentYear - i)
const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function StarIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  )
}

function KpiCard({ label, value, sub, valueClass, bgClass, borderClass, icon }: {
  label: string
  value: string
  sub: string
  valueClass: string
  bgClass: string
  borderClass: string
  icon: React.ReactNode
}) {
  return (
    <div className={`${bgClass} ${borderClass} border rounded-xl p-4`}>
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider leading-tight">{label}</p>
        <div className="ml-2 flex-shrink-0">{icon}</div>
      </div>
      <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
      <p className="text-xs text-gray-500 mt-1 leading-tight">{sub}</p>
    </div>
  )
}

function WithoutCertificationTable({ employees }: { employees: { id: string; full_name: string; email: string; department: string }[] }) {
  if (employees.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <svg className="w-12 h-12 text-green-200 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-gray-600 font-medium">All employees are certified</p>
        <p className="text-gray-400 text-sm mt-1">No one is missing a certification</p>
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">#</th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Employee</th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Email</th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Department</th>
            <th className="py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase text-right">Status</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((emp, i) => (
            <tr key={emp.id} className="border-b border-gray-100 hover:bg-red-50 cursor-pointer group">
              <td className="py-3 px-3 text-xs text-gray-400">{i + 1}</td>
              <td className="py-3 px-3">
                <Link
                  to={`/learning/employees/${emp.id}`}
                  state={{ employeeName: emp.full_name, department: emp.department, email: emp.email }}
                  className="flex items-center gap-2"
                >
                  <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-gray-500">{emp.full_name.charAt(0).toUpperCase()}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 group-hover:text-red-600">{emp.full_name}</span>
                </Link>
              </td>
              <td className="py-3 px-3 text-sm text-gray-500">{emp.email}</td>
              <td className="py-3 px-3 text-sm text-gray-500">{emp.department || '—'}</td>
              <td className="py-3 px-3 text-right">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                  Not Certified
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-gray-400 mt-3 text-right">{employees.length} employee{employees.length !== 1 ? 's' : ''} without certification</p>
    </div>
  )
}

function WithCertificationTable({
  employees,
  getCertificateUrl,
}: {
  employees: EmployeeWithCertification[]
  getCertificateUrl: (recordId: string) => string
}) {
  if (employees.length === 0) {
    return <p className="text-sm text-gray-400 text-center py-8">No certified employees found</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">#</th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Employee</th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Department</th>
            <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Certification</th>
            <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
            <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Expires</th>
            <th className="py-2.5 px-3"></th>
          </tr>
        </thead>
        <tbody>
          {employees.map((emp, i) => {
            const firstCert = emp.certifications[0]
            const isExpired = firstCert?.certification_expiry_date
              ? new Date(firstCert.certification_expiry_date) < new Date()
              : false
            const isExpiringSoon = firstCert?.certification_expiry_date && !isExpired
              ? (new Date(firstCert.certification_expiry_date).getTime() - Date.now()) < 60 * 24 * 60 * 60 * 1000
              : false
            return (
              <tr key={emp.id} className="border-b border-gray-100 hover:bg-green-50 cursor-pointer group">
                <td className="py-3 px-3 text-xs text-gray-400">{i + 1}</td>
                <td className="py-3 px-3">
                  <Link
                    to={`/learning/employees/${emp.id}`}
                    state={{ employeeName: emp.full_name, department: emp.department, email: emp.email }}
                    className="flex items-center gap-2"
                  >
                    <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-green-600">{emp.full_name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-red-600">{emp.full_name}</p>
                      {emp.certifications.length > 1 && (
                        <p className="text-xs text-gray-400">{emp.certifications.length} certifications</p>
                      )}
                    </div>
                  </Link>
                </td>
                <td className="py-3 px-3 text-sm text-gray-500">{emp.department || '—'}</td>
                <td className="py-3 px-3">
                  <Link
                    to={`/learning/trainings/${firstCert?.training_id}`}
                    className="text-sm font-medium text-gray-900 hover:text-red-600 block truncate max-w-[200px]"
                  >
                    {firstCert?.training_name}
                  </Link>
                  {firstCert?.provider && (
                    <p className="text-xs text-gray-400">{firstCert.provider}</p>
                  )}
                </td>
                <td className="py-3 px-3 text-sm text-gray-500 text-right whitespace-nowrap">
                  {firstCert?.start_date ? format(new Date(firstCert.start_date), 'dd MMM yyyy') : '—'}
                </td>
                <td className="py-3 px-3 text-right whitespace-nowrap">
                  {firstCert?.certification_expiry_date ? (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      isExpired ? 'bg-red-100 text-red-700' :
                      isExpiringSoon ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {format(new Date(firstCert.certification_expiry_date), 'dd MMM yyyy')}
                      {isExpired && ' (expired)'}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">No expiry</span>
                  )}
                </td>
                <td className="py-3 px-3">
                  {firstCert?.has_certificate_file && (
                    <a
                      href={getCertificateUrl(firstCert.record_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download
                    </a>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p className="text-xs text-gray-400 mt-3 text-right">{employees.length} certified employee{employees.length !== 1 ? 's' : ''}</p>
    </div>
  )
}

const TrainingReportPage = () => {
  const { user } = useAuth()
  const [filters, setFilters] = useState<TrainingReportFilters>({ year: currentYear })
  const [certTab, setCertTab] = useState<'with' | 'without'>('without')
  const [certSearch, setCertSearch] = useState('')

  const hasAccess =
    user?.roles?.some(r => ['admin', 'hr'].includes(r.name.toLowerCase())) ||
    user?.division_name?.toLowerCase().includes('human')

  const { data: reportData, isLoading } = useQuery({
    queryKey: ['trainingReport', filters],
    queryFn: () => learningApi.getComprehensiveReport(filters),
    enabled: hasAccess,
  })
  const { data: employeesData } = useQuery({
    queryKey: ['trainingReportEmployees'],
    queryFn: () => learningApi.getReportEmployees(),
    enabled: hasAccess,
  })
  const { data: departmentsData } = useQuery({
    queryKey: ['trainingReportDepartments'],
    queryFn: () => learningApi.getReportDepartments(),
    enabled: hasAccess,
  })
  const { data: categoriesData } = useQuery({
    queryKey: ['trainingCategories'],
    queryFn: () => learningApi.getCategories(false),
    enabled: hasAccess,
  })

  const { data: certStatusData } = useQuery({
    queryKey: ['certificationStatus', filters.department],
    queryFn: () => learningApi.getCertificationStatus({
      department: filters.department,
    }),
    enabled: hasAccess,
    staleTime: 0,
  })

  const report = reportData?.data
  const employees = employeesData?.data || []
  const departments = departmentsData?.data || []
  const categories = categoriesData?.data || []
  const certStatus = certStatusData?.data

  const fmt = (v: number) => new Intl.NumberFormat('id-ID').format(v)

  const handleFilter = (key: keyof TrainingReportFilters, value: string | number | undefined) => {
    setFilters(prev => ({ ...prev, [key]: value || undefined }))
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to view the learning dashboard.</p>
          <Link to="/learning" className="mt-4 inline-block text-red-600 hover:text-red-700">
            &larr; Back to Learning
          </Link>
        </div>
      </div>
    )
  }

  if (isLoading) return <LoadingPage />

  const s = report?.summary
  const circumference = 2 * Math.PI * 42
  const compliancePct = s?.compliance_rate ?? 0
  const complianceStroke = compliancePct >= 80 ? '#16a34a' : compliancePct >= 50 ? '#ca8a04' : '#dc2626'
  const complianceTextColor = compliancePct >= 80 ? 'text-green-700' : compliancePct >= 50 ? 'text-yellow-700' : 'text-red-700'

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-rose-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center space-x-2 text-sm mb-3">
            <Link to="/learning" className="text-red-100 hover:text-white">Learning</Link>
            <span className="text-red-200">/</span>
            <span className="text-white">Dashboard</span>
          </nav>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Learning Dashboard</h1>
              <p className="text-red-100 mt-1">
                {filters.year ? `Year ${filters.year}` : 'All time'}
                {s ? ` · ${fmt(s.total_records)} records · ${s.unique_employees} employees trained` : ''}
              </p>
            </div>
            {s && (
              <div className="hidden md:flex items-center gap-8">
                <div className="text-right">
                  <p className="text-2xl font-bold">{fmt(s.total_training_hours)}h</p>
                  <p className="text-red-200 text-sm">Verified Hours</p>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${compliancePct >= 80 ? 'text-green-300' : 'text-yellow-300'}`}>
                    {compliancePct}%
                  </p>
                  <p className="text-red-200 text-sm">Compliance</p>
                </div>
              </div>
            )}
          </div>
          {/* Quick Actions */}
          <div className="flex flex-wrap items-center gap-2 mt-5">
            <Link
              to="/learning/all-records"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              All Records
            </Link>
            {s && s.total_pending > 0 && (
              <Link
                to="/learning/records?status=pending"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/80 hover:bg-amber-500 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                {s.total_pending} Pending Review
              </Link>
            )}
            <Link
              to="/learning/trainings"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
              Training Catalog
            </Link>
            <Link
              to="/learning/requirements"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
              Requirements
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Year</label>
              <select
                value={filters.year || ''}
                onChange={e => handleFilter('year', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Years</option>
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Department</label>
              <select
                value={filters.department || ''}
                onChange={e => handleFilter('department', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Departments</option>
                {departments.map(d => <option key={d.name} value={d.name}>{d.name} ({d.employee_count})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Category</label>
              <select
                value={filters.category_id || ''}
                onChange={e => handleFilter('category_id', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Employee</label>
              <select
                value={filters.employee_id || ''}
                onChange={e => handleFilter('employee_id', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Employees</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.full_name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Status</label>
              <select
                value={filters.verification_status || ''}
                onChange={e => handleFilter('verification_status', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {report && s && (
          <>
            {/* Pending Verification Alert */}
            {s.total_pending > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-amber-900">
                    {s.total_pending} training {s.total_pending === 1 ? 'record' : 'records'} pending verification
                  </p>
                  <p className="text-sm text-amber-700 mt-0.5">
                    These require HR review before counting toward compliance.
                  </p>
                </div>
                <Link
                  to="/learning/records?status=pending"
                  className="flex-shrink-0 px-4 py-2 bg-amber-600 text-white text-sm font-semibold rounded-lg hover:bg-amber-700 transition-colors"
                >
                  Review Now
                </Link>
              </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <KpiCard
                label="Total Records"
                value={fmt(s.total_records)}
                sub={`${s.unique_employees} employees · ${s.unique_trainings} trainings`}
                valueClass="text-gray-900"
                bgClass="bg-white"
                borderClass="border-gray-200"
                icon={
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                }
              />
              <KpiCard
                label="Verified"
                value={fmt(s.total_verified)}
                sub={s.total_records > 0 ? `${Math.round(s.total_verified / s.total_records * 100)}% of total` : '—'}
                valueClass="text-green-700"
                bgClass="bg-green-50"
                borderClass="border-green-100"
                icon={
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
              <KpiCard
                label="Pending Review"
                value={fmt(s.total_pending)}
                sub={s.total_rejected > 0 ? `${s.total_rejected} rejected` : 'None rejected'}
                valueClass={s.total_pending > 0 ? 'text-amber-700' : 'text-gray-500'}
                bgClass={s.total_pending > 0 ? 'bg-amber-50' : 'bg-white'}
                borderClass={s.total_pending > 0 ? 'border-amber-100' : 'border-gray-200'}
                icon={
                  <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
              <KpiCard
                label="Training Hours"
                value={`${fmt(s.total_training_hours)}h`}
                sub={s.unique_employees > 0 ? `${(s.total_training_hours / s.unique_employees).toFixed(1)}h avg / person` : '—'}
                valueClass="text-red-700"
                bgClass="bg-red-50"
                borderClass="border-red-100"
                icon={
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                }
              />
              <KpiCard
                label="Compliance Rate"
                value={`${compliancePct}%`}
                sub={`${s.total_compliant} / ${s.total_with_requirements} compliant`}
                valueClass={complianceTextColor}
                bgClass={compliancePct >= 80 ? 'bg-green-50' : compliancePct >= 50 ? 'bg-yellow-50' : 'bg-red-50'}
                borderClass={compliancePct >= 80 ? 'border-green-100' : compliancePct >= 50 ? 'border-yellow-100' : 'border-red-100'}
                icon={
                  <svg className="w-5 h-5 text-current opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                }
              />
            </div>

            {/* Compliance Gauge + Needs Attention */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Gauge */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-5">
                  Compliance Overview
                </h3>
                <div className="flex flex-col items-center">
                  <div className="relative flex items-center justify-center w-36 h-36">
                    <svg className="w-36 h-36 -rotate-90" viewBox="0 0 100 100">
                      <circle cx="50" cy="50" r="42" fill="none" stroke="#f3f4f6" strokeWidth="10" />
                      <circle
                        cx="50" cy="50" r="42" fill="none"
                        stroke={complianceStroke}
                        strokeWidth="10"
                        strokeLinecap="round"
                        strokeDasharray={`${circumference}`}
                        strokeDashoffset={`${circumference * (1 - compliancePct / 100)}`}
                        style={{ transition: 'stroke-dashoffset 0.7s ease' }}
                      />
                    </svg>
                    <div className="absolute text-center">
                      <p className={`text-3xl font-bold ${complianceTextColor}`}>{compliancePct}%</p>
                    </div>
                  </div>
                  <div className="w-full mt-5 space-y-2.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Compliant</span>
                      <span className="font-semibold text-green-700">{s.total_compliant}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Non-compliant</span>
                      <span className="font-semibold text-red-600">{s.total_with_requirements - s.total_compliant}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-100">
                      <span className="text-gray-500">With requirements</span>
                      <span className="font-semibold text-gray-900">{s.total_with_requirements}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Needs Attention */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Needs Attention</h3>
                  <span className="text-xs text-gray-400">{report.least_compliant.length} employees below target</span>
                </div>
                {report.least_compliant.length > 0 ? (
                  <div className="space-y-3">
                    {report.least_compliant.slice(0, 7).map((emp, i) => (
                      <div key={`${emp.id}-${i}`} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-red-500">
                            {emp.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                            <span className={`text-xs font-semibold ml-2 flex-shrink-0 ${emp.completion_percentage >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {emp.completion_percentage}%
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${emp.completion_percentage >= 50 ? 'bg-yellow-400' : 'bg-red-400'}`}
                                style={{ width: `${Math.min(emp.completion_percentage, 100)}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-400 flex-shrink-0">
                              {emp.completed_hours}h / {emp.required_hours}h
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-center">
                    <svg className="w-12 h-12 text-green-200 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-gray-600 font-medium">All employees are on track</p>
                    <p className="text-gray-400 text-sm mt-1">No compliance issues detected</p>
                  </div>
                )}
              </div>
            </div>

            {/* Category Breakdown + Rating Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-5">Training by Category</h3>
                {report.by_category.length > 0 ? (
                  <div className="space-y-4">
                    {(() => {
                      const maxCount = Math.max(...report.by_category.map(c => c.count), 1)
                      return report.by_category.map(cat => (
                        <div key={cat.id}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-medium text-gray-700">{cat.name}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-gray-400">{fmt(cat.hours)}h verified</span>
                              <span className="text-sm font-semibold text-gray-900 w-6 text-right">{cat.count}</span>
                            </div>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div
                              className="bg-red-500 h-2 rounded-full"
                              style={{ width: `${(cat.count / maxCount) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))
                    })()}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">No category data available</p>
                )}
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-5">Rating Distribution</h3>
                <div className="space-y-3">
                  {[5, 4, 3, 2, 1].map(rating => {
                    const count = report.rating_distribution[rating] || 0
                    const total = Object.values(report.rating_distribution).reduce((a, b) => a + b, 0)
                    const pct = total > 0 ? (count / total) * 100 : 0
                    return (
                      <div key={rating} className="flex items-center gap-2">
                        <div className="flex items-center gap-0.5 w-10 flex-shrink-0">
                          <span className="text-xs text-gray-600">{rating}</span>
                          <StarIcon className="w-3 h-3 text-yellow-400" />
                        </div>
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div className="bg-yellow-400 h-2 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-gray-400 w-6 text-right flex-shrink-0">{count}</span>
                      </div>
                    )
                  })}
                </div>
                {s.average_rating > 0 && (
                  <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-center gap-2">
                    <StarIcon className="w-5 h-5 text-yellow-400" />
                    <span className="text-2xl font-bold text-gray-900">{s.average_rating.toFixed(1)}</span>
                    <span className="text-sm text-gray-400">/ 5.0</span>
                  </div>
                )}
              </div>
            </div>

            {/* Monthly Trends — Stacked Bars */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Monthly Trends</h3>
                <div className="flex items-center gap-5">
                  {[
                    { color: 'bg-green-500', label: 'Verified' },
                    { color: 'bg-amber-400', label: 'Pending' },
                    { color: 'bg-red-400', label: 'Rejected' },
                  ].map(item => (
                    <span key={item.label} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <span className={`inline-block w-2.5 h-2.5 rounded-sm ${item.color}`} />
                      {item.label}
                    </span>
                  ))}
                </div>
              </div>
              <div className="overflow-x-auto">
                <div className="flex items-end gap-1 min-w-[600px]" style={{ height: '200px' }}>
                  {report.monthly_trends.map(trend => {
                    const BAR_MAX_H = 150
                    const maxCount = Math.max(...report.monthly_trends.map(t => t.count), 1)
                    const scale = BAR_MAX_H / maxCount
                    const vH = Math.round(trend.verified * scale)
                    const pH = Math.round(trend.pending * scale)
                    const rH = Math.round(trend.rejected * scale)
                    return (
                      <div
                        key={trend.month}
                        className="flex-1 flex flex-col items-center"
                        style={{ height: '200px' }}
                        title={`${MON[trend.month - 1]}: ${trend.count} total (${trend.verified} verified, ${trend.pending} pending, ${trend.rejected} rejected)`}
                      >
                        <div className="flex-shrink-0 text-center h-5">
                          {trend.count > 0 && (
                            <span className="text-xs font-medium text-gray-600">{trend.count}</span>
                          )}
                        </div>
                        <div
                          className="w-full flex items-end justify-center"
                          style={{ height: `${BAR_MAX_H}px` }}
                        >
                          {trend.count > 0 ? (
                            <div className="w-full max-w-[32px] flex flex-col rounded-t overflow-hidden">
                              {rH > 0 && <div className="bg-red-400 w-full" style={{ height: `${rH}px` }} />}
                              {pH > 0 && <div className="bg-amber-400 w-full" style={{ height: `${pH}px` }} />}
                              {vH > 0 && <div className="bg-green-500 w-full" style={{ height: `${vH}px` }} />}
                            </div>
                          ) : (
                            <div className="w-full max-w-[32px] bg-gray-100 rounded" style={{ height: '2px' }} />
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5 flex-shrink-0">{MON[trend.month - 1]}</p>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Leaderboards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Top Trainings */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Most Popular Trainings</h3>
                <div className="space-y-1">
                  {report.top_trainings.slice(0, 5).map((t, i) => (
                    <div key={t.id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        i === 0 ? 'bg-yellow-100 text-yellow-700' :
                        i === 1 ? 'bg-gray-200 text-gray-600' :
                        i === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/learning/trainings/${t.id}`}
                          className="text-sm font-medium text-gray-900 hover:text-red-600 truncate block"
                        >
                          {t.name}
                        </Link>
                        <p className="text-xs text-gray-400">{t.category || 'No category'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900">{t.count}</p>
                        {t.avg_rating > 0 && (
                          <div className="flex items-center justify-end gap-0.5">
                            <StarIcon className="w-3 h-3 text-yellow-400" />
                            <span className="text-xs text-gray-400">{t.avg_rating}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {report.top_trainings.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-6">No training data available</p>
                  )}
                </div>
              </div>

              {/* Top Learners */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Top Learners by Hours</h3>
                <div className="space-y-1">
                  {report.top_employees.slice(0, 5).map((emp, i) => (
                    <div key={`top-${emp.id}-${i}`} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        i === 0 ? 'bg-yellow-100 text-yellow-700' :
                        i === 1 ? 'bg-gray-200 text-gray-600' :
                        i === 2 ? 'bg-orange-100 text-orange-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{emp.name}</p>
                        <p className="text-xs text-gray-400">{emp.department || 'No department'}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-red-600">{fmt(emp.hours)}h</p>
                        <p className="text-xs text-gray-400">{emp.verified_count} verified</p>
                      </div>
                    </div>
                  ))}
                  {report.top_employees.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-6">No employee data available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Certification Status */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Certification Status</h3>
                  {certStatus && (
                    <p className="text-sm text-gray-500 mt-0.5">
                      {certStatus.certified_count} certified · {certStatus.not_certified_count} not certified
                      {' '}(out of {certStatus.total_employees} employees)
                    </p>
                  )}
                </div>
                <div className="flex bg-gray-100 rounded-lg p-0.5">
                  <button
                    onClick={() => setCertTab('without')}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      certTab === 'without'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Not Certified
                    {certStatus && (
                      <span className="ml-1.5 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">
                        {certStatus.not_certified_count}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setCertTab('with')}
                    className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      certTab === 'with'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Certified
                    {certStatus && (
                      <span className="ml-1.5 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                        {certStatus.certified_count}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search by name or department..."
                  value={certSearch}
                  onChange={e => setCertSearch(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
              </div>

              {certTab === 'without' ? (
                <WithoutCertificationTable
                  employees={(certStatus?.without_certification || []).filter(e =>
                    !certSearch ||
                    e.full_name.toLowerCase().includes(certSearch.toLowerCase()) ||
                    e.department.toLowerCase().includes(certSearch.toLowerCase())
                  )}
                />
              ) : (
                <WithCertificationTable
                  employees={(certStatus?.with_certification || []).filter(e =>
                    !certSearch ||
                    e.full_name.toLowerCase().includes(certSearch.toLowerCase()) ||
                    e.department.toLowerCase().includes(certSearch.toLowerCase())
                  )}
                  getCertificateUrl={learningApi.getCertificateUrl}
                />
              )}
            </div>

            {/* Department Overview Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Department Overview</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Department</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Employees</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Records</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Verified Hours</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Avg h / Person</th>
                      <th className="text-right py-2.5 px-3 text-xs font-semibold text-gray-500 uppercase">Records / Person</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...report.by_department].sort((a, b) => b.hours - a.hours).map(dept => (
                      <tr key={dept.department} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-3 text-sm font-medium text-gray-900">{dept.department}</td>
                        <td className="py-3 px-3 text-sm text-gray-600 text-right">{dept.employee_count}</td>
                        <td className="py-3 px-3 text-sm text-gray-600 text-right">{dept.count}</td>
                        <td className="py-3 px-3 text-sm font-medium text-red-600 text-right">
                          {fmt(dept.hours)}h
                        </td>
                        <td className="py-3 px-3 text-sm text-right">
                          <span className="font-semibold text-gray-900">
                            {dept.employee_count > 0 ? (dept.hours / dept.employee_count).toFixed(1) : 0}h
                          </span>
                        </td>
                        <td className="py-3 px-3 text-sm text-gray-600 text-right">
                          {dept.employee_count > 0 ? (dept.count / dept.employee_count).toFixed(1) : 0}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {report.by_department.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-8">No department data available</p>
                )}
              </div>
            </div>

            {/* Delivery Mode + Training Type */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-5">By Delivery Mode</h3>
                <div className="space-y-4">
                  {report.by_delivery_mode.map(mode => {
                    const maxC = Math.max(...report.by_delivery_mode.map(m => m.count), 1)
                    const colorMap: Record<string, string> = {
                      online: 'bg-blue-500',
                      in_person: 'bg-green-500',
                      hybrid: 'bg-purple-500',
                    }
                    return (
                      <div key={mode.mode}>
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm text-gray-700 capitalize">{mode.mode.replace('_', ' ')}</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {mode.count}
                            <span className="text-xs font-normal text-gray-400 ml-1">· {fmt(mode.hours)}h</span>
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className={`${colorMap[mode.mode] || 'bg-gray-400'} h-2 rounded-full`}
                            style={{ width: `${(mode.count / maxC) * 100}%` }}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {report.by_training_type && report.by_training_type.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-5">By Training Type</h3>
                  <div className="space-y-4">
                    {[...report.by_training_type].sort((a, b) => b.count - a.count).map(t => {
                      const maxC = Math.max(...report.by_training_type.map(x => x.count), 1)
                      return (
                        <div key={t.type}>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm text-gray-700 capitalize">{t.type.replace('_', ' ')}</span>
                            <span className="text-sm font-semibold text-gray-900">
                              {t.count}
                              <span className="text-xs font-normal text-gray-400 ml-1">· {fmt(t.hours)}h</span>
                            </span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2">
                            <div
                              className="bg-red-400 h-2 rounded-full"
                              style={{ width: `${(t.count / maxC) * 100}%` }}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default TrainingReportPage
